import React, { useState } from 'react'
import {
  Box,
  Container,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  ListItemIcon,
  IconButton,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material'
import {
  Notifications,
  NotificationsActive,
  CheckCircle,
  Info,
  Warning,
  Error as ErrorIcon,
  Delete,
  MarkEmailRead,
  Close,
} from '@mui/icons-material'
import {
  useGetNotificationsQuery,
  useGetWebPushConfigQuery,
  useSubscribeWebPushMutation,
  useUnsubscribeWebPushMutation,
  useTestWebPushMutation,
  useMarkAsReadMutation,
  useMarkAllAsReadMutation,
  useDeleteNotificationMutation,
  type Notification,
} from '../store/api/notificationsApi'
import { PageHeader } from '../components/PageHeader'
import { getFilterPaperSx } from '../theme/sectionSurfaces'

function urlBase64ToUint8Array(base64String: string) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(base64)
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)))
}

const getNotificationIcon = (type: string) => {
  switch (type) {
    case 'success':
      return <CheckCircle color="success" />
    case 'warning':
      return <Warning color="warning" />
    case 'error':
      return <ErrorIcon color="error" />
    default:
      return <Info color="info" />
  }
}

const getNotificationColor = (type: string) => {
  switch (type) {
    case 'success':
      return 'success'
    case 'warning':
      return 'warning'
    case 'error':
      return 'error'
    default:
      return 'info'
  }
}

export default function NotificationsPage() {
  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null)
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  })

  const { data: notifications = [], isLoading, refetch } = useGetNotificationsQuery(
    {},
    {
      pollingInterval: 20000, // Poll every 20 seconds
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    }
  )
  const [markAsRead] = useMarkAsReadMutation()
  const [markAllAsRead] = useMarkAllAsReadMutation()
  const [deleteNotification] = useDeleteNotificationMutation()
  const { data: webPushConfig } = useGetWebPushConfigQuery()
  const [subscribeWebPush, { isLoading: subscribingPush }] = useSubscribeWebPushMutation()
  const [unsubscribeWebPush, { isLoading: unsubscribingPush }] = useUnsubscribeWebPushMutation()
  const [testWebPush, { isLoading: testingPush }] = useTestWebPushMutation()

  const unreadCount = notifications.filter((n) => !n.read).length

  const handleEnableWebPush = async () => {
    if (!webPushConfig?.enabled || !webPushConfig.publicKey) {
      setSnackbar({
        open: true,
        message: 'Máy chủ chưa cấu hình WEBPUSH_VAPID_PUBLIC_KEY — xem docs triển khai.',
        severity: 'error',
      })
      return
    }
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      setSnackbar({ open: true, message: 'Trình duyệt không hỗ trợ Web Push.', severity: 'error' })
      return
    }
    try {
      const perm = await Notification.requestPermission()
      if (perm !== 'granted') {
        setSnackbar({ open: true, message: 'Cần quyền thông báo từ trình duyệt.', severity: 'error' })
        return
      }
      const reg = await navigator.serviceWorker.register('/sw.js')
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(webPushConfig.publicKey),
      })
      const j = sub.toJSON()
      if (!j.endpoint || !j.keys?.p256dh || !j.keys?.auth) {
        throw new Error('Subscription không đủ khóa')
      }
      await subscribeWebPush({
        endpoint: j.endpoint,
        keys: { p256dh: j.keys.p256dh, auth: j.keys.auth },
      }).unwrap()
      setSnackbar({ open: true, message: 'Đã bật thông báo đẩy (Web Push)', severity: 'success' })
    } catch (e: any) {
      setSnackbar({
        open: true,
        message: e?.data?.detail || e?.message || 'Đăng ký Web Push thất bại',
        severity: 'error',
      })
    }
  }

  const handleDisableWebPush = async () => {
    try {
      const reg = await navigator.serviceWorker.getRegistration()
      const sub = await reg?.pushManager.getSubscription()
      if (sub) await sub.unsubscribe()
      await unsubscribeWebPush().unwrap()
      setSnackbar({ open: true, message: 'Đã tắt Web Push', severity: 'success' })
    } catch (e: any) {
      setSnackbar({
        open: true,
        message: e?.data?.detail || e?.message || 'Huỷ đăng ký thất bại',
        severity: 'error',
      })
    }
  }

  const handleTestWebPush = async () => {
    try {
      await testWebPush().unwrap()
      setSnackbar({ open: true, message: 'Đã gửi thử — kiểm tra thông báo hệ điều hành.', severity: 'success' })
    } catch (e: any) {
      setSnackbar({
        open: true,
        message: e?.data?.detail || e?.message || 'Gửi thử thất bại',
        severity: 'error',
      })
    }
  }

  const handleMarkAsRead = async (notificationId: string) => {
    try {
      await markAsRead(notificationId).unwrap()
      setSnackbar({ open: true, message: 'Đã đánh dấu đã đọc', severity: 'success' })
      refetch()
    } catch (error) {
      setSnackbar({ open: true, message: 'Lỗi khi đánh dấu đã đọc', severity: 'error' })
    }
  }

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead().unwrap()
      setSnackbar({ open: true, message: 'Đã đánh dấu tất cả đã đọc', severity: 'success' })
      refetch()
    } catch (error) {
      setSnackbar({ open: true, message: 'Lỗi khi đánh dấu tất cả đã đọc', severity: 'error' })
    }
  }

  const handleDelete = async (notificationId: string) => {
    try {
      await deleteNotification(notificationId).unwrap()
      setSnackbar({ open: true, message: 'Đã xóa thông báo', severity: 'success' })
      refetch()
      if (selectedNotification?.id === notificationId) {
        setSelectedNotification(null)
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Lỗi khi xóa thông báo', severity: 'error' })
    }
  }

  // View notification function - matches moduleFunctions.ts
  const handleViewNotification = (notification: Notification) => {
    setSelectedNotification(notification)
    if (!notification.read && notification.id) {
      handleMarkAsRead(notification.id)
    }
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return ''
    try {
      const date = new Date(dateString)
      const day = String(date.getDate()).padStart(2, '0')
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const year = date.getFullYear()
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      return `${day}/${month}/${year} ${hours}:${minutes}`
    } catch {
      return dateString
    }
  }

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 4 } }}>
      <PageHeader
        title="Thông báo"
        subtitle="Thông báo trong hệ thống và tùy chọn Web Push khi có sự kiện mới."
        icon={<Notifications />}
        actions={
          unreadCount > 0 ? (
            <Button variant="outlined" color="primary" startIcon={<MarkEmailRead />} onClick={handleMarkAllAsRead}>
              Đánh dấu tất cả đã đọc ({unreadCount})
            </Button>
          ) : null
        }
      />

      <Paper elevation={0} sx={(theme) => getFilterPaperSx(theme)}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Thông báo đẩy (Web Push — P4-M10-2)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Cần HTTPS (hoặc localhost). Backend cần cặp khóa VAPID; khi có thông báo in-app mới, server sẽ cố gửi push
          nếu user đã đăng ký.
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Button
            variant="contained"
            onClick={handleEnableWebPush}
            disabled={subscribingPush || !webPushConfig?.enabled}
          >
            {subscribingPush ? <CircularProgress size={20} /> : 'Bật Web Push'}
          </Button>
          <Button variant="outlined" onClick={handleDisableWebPush} disabled={unsubscribingPush}>
            {unsubscribingPush ? <CircularProgress size={20} /> : 'Tắt Web Push'}
          </Button>
          <Button variant="outlined" onClick={handleTestWebPush} disabled={testingPush}>
            {testingPush ? <CircularProgress size={20} /> : 'Gửi thử'}
          </Button>
        </Box>
        {webPushConfig && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            Public key: {webPushConfig.enabled ? 'có' : 'chưa'} · Gửi từ server:{' '}
            {webPushConfig.sendingEnabled ? 'đủ VAPID' : 'thiếu private key'}
          </Typography>
        )}
      </Paper>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : notifications.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Notifications sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Không có thông báo nào
          </Typography>
        </Paper>
      ) : (
        <Paper>
          <List>
            {notifications.map((notification, index) => (
              <React.Fragment key={notification.id || index}>
                <ListItem
                  disablePadding
                  sx={{
                    backgroundColor: notification.read ? 'transparent' : 'action.hover',
                    '&:hover': {
                      backgroundColor: 'action.selected',
                    },
                  }}
                >
                  <ListItemButton onClick={() => handleViewNotification(notification)}>
                    <ListItemIcon>{getNotificationIcon(notification.type)}</ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle1" fontWeight={notification.read ? 400 : 600}>
                            {notification.title}
                          </Typography>
                          {!notification.read && (
                            <Chip
                              label="Mới"
                              size="small"
                              color="primary"
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {notification.message}
                          </Typography>
                          {notification.created_at && (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                              {formatDate(notification.created_at)}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {notification.metadata?.meeting_link && (
                        <Chip
                          label="Phòng trực tuyến"
                          size="small"
                          color="info"
                          onClick={(e) => {
                            e.stopPropagation()
                            window.open(notification.metadata?.meeting_link, '_blank')
                          }}
                        />
                      )}
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation()
                          if (notification.id) {
                            handleDelete(notification.id)
                          }
                        }}
                        color="error"
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  </ListItemButton>
                </ListItem>
                {index < notifications.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Paper>
      )}

      {/* Notification Detail Dialog */}
      <Dialog
        open={!!selectedNotification}
        onClose={() => setSelectedNotification(null)}
        maxWidth="md"
        fullWidth
      >
        {selectedNotification && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {getNotificationIcon(selectedNotification.type)}
                <Typography variant="h6">{selectedNotification.title}</Typography>
                <Chip
                  label={selectedNotification.type}
                  size="small"
                  color={getNotificationColor(selectedNotification.type) as any}
                  sx={{ ml: 'auto' }}
                />
              </Box>
            </DialogTitle>
            <DialogContent>
              <Typography variant="body1" paragraph>
                {selectedNotification.message}
              </Typography>
              {selectedNotification.metadata?.meeting_link && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Link tham gia phòng trực tuyến:
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    href={selectedNotification.metadata.meeting_link}
                    target="_blank"
                    fullWidth
                    sx={{ mt: 1 }}
                  >
                    Tham gia cuộc họp
                  </Button>
                </Box>
              )}
              {selectedNotification.created_at && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                  Thời gian: {formatDate(selectedNotification.created_at)}
                </Typography>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelectedNotification(null)}>Đóng</Button>
              {selectedNotification.metadata?.meeting_link && (
                <Button
                  variant="contained"
                  href={selectedNotification.metadata.meeting_link}
                  target="_blank"
                >
                  Tham gia cuộc họp
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  )
}

